import { useQuery } from "@apollo/client";
import { Icon, SearchIcon, AddIcon } from "@chakra-ui/icons";
import {
  useMediaQuery,
  Box,
  useDisclosure,
  SimpleGrid,
  Flex,
  InputGroup,
  InputLeftAddon,
  Input,
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  ButtonGroup,
  IconButton,
  Stack,
  HStack,
  MenuItem,
  Center,
  Spinner,
  MenuDivider,
} from "@chakra-ui/react";
import { debounce, get } from "lodash";
import React, { useCallback, useContext, useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaSync } from "react-icons/fa";
import { MdFilterList } from "react-icons/md";
import { AuthContext } from "../../context/AuthContext";
import { GET_USERS } from "../../pages/admin/queries";
import Pagination from "../common/Pagination";
import UserMassCreate from "../modals/user/create/UserMassCreate";
import UsersTableContainer from "./UsersTableContainer";

type UsersTableProps = {};

const UsersTable = (props: UsersTableProps) => {
  const { auth } = useContext(AuthContext);
  const isAdmin = auth.role === "ADMIN";

  const [isLargerThan900] = useMediaQuery("(min-width: 900px)");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [refetchLoading, setRefetchLoading] = useState(false);

  const [role, setRoles] = useState<any>(
    isAdmin ? ["ESTABLISHMENT", "INDIVIDUAL", "ADMIN"] : []
  );
  const [status, setStatus] = useState<any>(["active"]);

  const { data, loading, refetch } = useQuery(GET_USERS, {
    variables: {
      args: {
        role,
        status,
        query: search,
        pagination: {
          page,
          size: 15,
        },
      },
    },
    fetchPolicy: "network-only",
  });

  // const {
  //   isOpen: isCreateModalOpen,
  //   onOpen: onOpenCreateModal,
  //   onClose: onCloseCreateModal,
  // } = useDisclosure();

  const {
    isOpen: isMassCreateModalOpen,
    onOpen: onOpenMassCreateModal,
    onClose: onCloseMassCreateModal,
  } = useDisclosure();

  const debounceFn = useCallback(
    debounce((val) => {
      setSearch(val);
      setPage(1);
    }, 500),
    []
  );

  const onSearch = (val: any) => {
    debounceFn(val);
  };

  const users = get(data, "users.items", []);
  const meta = get(data, "users.meta", {});

  const refetchUsers = () => {
    setRefetchLoading(true);
    refetch()
      .then(() => setRefetchLoading(false))
      .finally(() => setRefetchLoading(false));
  };

  return (
    <>
      {isLargerThan900 ? (
        <SimpleGrid columns={[1, 2]}>
          <Pagination
            meta={meta}
            page={page}
            setPage={setPage}
            isDisabled={loading}
          />
          <Flex alignItems="center" justifyContent="space-between">
            <InputGroup mx="2">
              <InputLeftAddon
                rounded="md"
                children={<Icon size="2" as={SearchIcon} />}
              />
              <Input
                rounded="md"
                focusBorderColor="green.500"
                onChange={(e) => {
                  onSearch(e.target.value);
                  setPage(1);
                }}
              />
            </InputGroup>

            <Flex>
              <Menu closeOnSelect={false}>
                <MenuButton as={Button} rightIcon={<MdFilterList />}>
                  Filters
                </MenuButton>
                <MenuList>
                  <Stack p="2">
                    {isAdmin && (
                      <>
                        <MenuOptionGroup
                          title="Role"
                          type="checkbox"
                          onChange={(e) => {
                            setRoles(e);
                            setPage(1);
                          }}
                          textAlign="start"
                          defaultValue={role}
                        >
                          <MenuItemOption value="INDIVIDUAL">
                            Individual
                          </MenuItemOption>
                          <MenuItemOption value="ESTABLISHMENT">
                            Establishment
                          </MenuItemOption>
                          <MenuItemOption value="ADMIN">Admin</MenuItemOption>
                        </MenuOptionGroup>
                        <MenuDivider />
                      </>
                    )}

                    <MenuOptionGroup
                      title="Status"
                      type="checkbox"
                      onChange={(e) => {
                        setStatus(e);
                        setPage(1);
                      }}
                      textAlign="start"
                      defaultValue={status}
                    >
                      <MenuItemOption value="active">Active</MenuItemOption>
                      <MenuItemOption value="inactive">Inactive</MenuItemOption>
                      <MenuItemOption value="removed">Removed</MenuItemOption>
                      <MenuItemOption value="review">
                        Under Review
                      </MenuItemOption>
                    </MenuOptionGroup>
                  </Stack>
                </MenuList>
              </Menu>
              <ButtonGroup isAttached mx="2">
                <Button onClick={onOpenMassCreateModal} leftIcon={<AddIcon />}>
                  New
                </Button>
                {/* <IconButton
                  onClick={onOpenMassCreateModal}
                  aria-label="More"
                  bg="gray.200"
                  icon={<AddIcon />}
                /> */}

                <ButtonGroup>
                  {refetchLoading ? (
                    <Button ml="2">
                      <Spinner size="sm" />
                    </Button>
                  ) : (
                    <IconButton
                      aria-label="More"
                      bg="gray.200"
                      icon={<FaSync />}
                      isDisabled={loading}
                      onClick={refetchUsers}
                      ml="2"
                    />
                  )}
                </ButtonGroup>
              </ButtonGroup>
            </Flex>
          </Flex>
        </SimpleGrid>
      ) : (
        <Stack>
          <InputGroup p="3">
            <InputLeftAddon
              rounded="md"
              children={<Icon size="2" as={SearchIcon} />}
            />

            <Input
              rounded="md"
              focusBorderColor="green.500"
              onChange={(e) => {
                onSearch(e.target.value);
                setPage(1);
              }}
            />
          </InputGroup>

          <HStack justifyContent="space-between">
            <Pagination
              meta={meta}
              page={page}
              setPage={setPage}
              isDisabled={loading}
              showBottomTotal={true}
              showTotal={false}
              size="md"
            />
            <Box p="3">
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={IconButton}
                  size="md"
                  icon={<MdFilterList />}
                  mr="2"
                />

                <MenuList>
                  <Stack p="2">
                    {isAdmin && (
                      <>
                        <MenuOptionGroup
                          title="Role"
                          type="checkbox"
                          onChange={(e) => {
                            setRoles(e);
                            setPage(1);
                          }}
                          textAlign="start"
                        >
                          <MenuItemOption value="INDIVIDUAL">
                            Individual
                          </MenuItemOption>
                          <MenuItemOption value="ESTABLISHMENT">
                            Establishment
                          </MenuItemOption>
                          <MenuItemOption value="ADMIN">Admin</MenuItemOption>
                        </MenuOptionGroup>
                        <MenuDivider />
                      </>
                    )}

                    <MenuOptionGroup
                      title="Status"
                      type="checkbox"
                      onChange={(e) => {
                        setStatus(e);
                        setPage(1);
                      }}
                      textAlign="start"
                    >
                      <MenuItemOption value="active">Active</MenuItemOption>
                      <MenuItemOption value="inactive">Inactive</MenuItemOption>
                      <MenuItemOption value="removed">Removed</MenuItemOption>
                    </MenuOptionGroup>
                  </Stack>
                </MenuList>
              </Menu>
              <Menu size="md">
                <MenuButton
                  as={IconButton}
                  icon={<BsThreeDotsVertical />}
                  size="md"
                />

                <MenuList>
                  {/* <MenuItem onClick={onOpenMassUpdateModal}>Update</MenuItem> */}
                  <MenuItem onClick={onOpenMassCreateModal} icon={<AddIcon />}>
                    New
                  </MenuItem>
                  {/* 
                  <MenuItem
                    onClick={onOpenMassCreateModal}
                    icon={<MdExposurePlus2 />}
                  >
                    Mass Create
                  </MenuItem> */}

                  <MenuItem onClick={refetchUsers} icon={<FaSync />}>
                    Refresh
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
            {/* <ButtonGroup isAttached size="md">
              <Button onClick={onOpenCreateMpdal}>New</Button>
              <IconButton
                onClick={onOpenMassCreateMpdal}
                aria-label="More"
                bg="gray.200"
                icon={<AddIcon />}
              />

              <ButtonGroup size="md">
                <IconButton
                  aria-label="More"
                  bg="gray.200"
                  icon={<FaSync />}
                  isLoading={loading}
                  onClick={() => refetch()}
                />
              </ButtonGroup>
            </ButtonGroup> */}
          </HStack>
        </Stack>
      )}

      {/* <UserCreate
        isOpen={isCreateModalOpen}
        onOpen={onOpenCreateModal}
        onClose={onCloseCreateModal}
        refetch={refetchUsers}
      /> */}

      <UserMassCreate
        isOpen={isMassCreateModalOpen}
        onOpen={onOpenMassCreateModal}
        onClose={onCloseMassCreateModal}
        refetch={refetchUsers}
      />

      <Box mt="6">
        {loading ? (
          <Center py="5">
            <Spinner />
          </Center>
        ) : isLargerThan900 ? (
          <>
            {refetchLoading && (
              <Center py="5">
                <Spinner />
              </Center>
            )}
            <Box p="5" height="80vh" overflowX="auto">
              <UsersTableContainer
                isAdmin={isAdmin}
                users={users}
                refetch={refetchUsers}
              />
            </Box>
          </>
        ) : (
          <UsersTableContainer
            isAdmin={isAdmin}
            users={users}
            refetch={refetchUsers}
          />
        )}
      </Box>
    </>
  );
};

export default UsersTable;
